import { TableWithStoredSettingsAndLayoutSettings } from '@Components/Tables/TableWithStoredSettingsAndLayoutSettings';
import { DynamicExportCategory, DynamicExportKey } from '@Constants/dynamicExportConstants';
import { Permission } from '@Constants/Permission.g';
import { exportColumns, exportInvoiceColumns, initialInvoiceSorting } from '@Constants/TableColumns';
import { useHasPermission } from '@Contexts/appContext';
import { useLoadExportableInvoices } from '@Hooks/Billing';
import { useDynamicExportOptions, useLoadExportRuns, useScheduleExportRun } from '@Hooks/Exports';
import { useRemoteCacheInvalidation } from '@Hooks/useRemoteCacheInvalidation';
import { type IHasTableModes } from '@Hooks/useTableModes';
import { type ApiDynamicExportRun, type ApiInvoice, EnumFrontendLayoutSetting } from '@Model';
import { type InvoiceTableModes } from '@Modules/Billing';
import { useExportRunDetailModal } from '@Modules/Documents';
import {
    Badge,
    Button,
    Card,
    defaultIconSize,
    FloatingToolbar,
    SingleSelect,
    Table,
    type ToolbarOptions,
    useTableRefetch,
} from '@synqo/frontend-component-library';
import { IconAbacus } from '@tabler/icons-react';
import { useCallback, useMemo, useState } from 'react';

interface IFrameListExportInvoicesProps extends IHasTableModes<InvoiceTableModes> {}

const emptyItems: ApiInvoice[] = [];

export function FrameListExportInvoices({
    tableModeValue,
    setTableMode,
    availableTableModes,
}: IFrameListExportInvoicesProps) {
    const hasPermission = useHasPermission();
    const openRunDetail = useExportRunDetailModal();

    const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([]);
    const { refetchRef: invoicesRefetchRef, refetchTable: refetchInvoicesTable } = useTableRefetch();
    const { refetchRef: exportRefetchRef, refetchTable: refetchExportTable } = useTableRefetch();

    useRemoteCacheInvalidation('exports', refetchExportTable);
    useRemoteCacheInvalidation('invoices', refetchInvoicesTable);

    const hasSelectedInvoices = selectedInvoiceIds.length > 0;

    const invoicesListApi = useLoadExportableInvoices();
    const exportsListApi = useLoadExportRuns(DynamicExportCategory.Invoices, DynamicExportKey.Accounting);

    const { scheduleExport, isExporting } = useScheduleExportRun();
    const { hasExportOptions, exportOptions } = useDynamicExportOptions(
        DynamicExportCategory.Invoices,
        DynamicExportKey.Accounting,
    );

    const handleExport = useCallback(async () => {
        const firstExportOption = exportOptions[0];
        if (!firstExportOption || selectedInvoiceIds.length === 0) {
            return;
        }

        await scheduleExport({
            exportDefinitionId: firstExportOption.id,
            selectedIds: selectedInvoiceIds,
        });

        setSelectedInvoiceIds([]);
        refetchExportTable();
    }, [scheduleExport, refetchExportTable, exportOptions, selectedInvoiceIds]);

    const objectSelector = useMemo(
        () => ({
            locked: false,
            selectedObjectIds: selectedInvoiceIds,
            setSelectedObjectIds: setSelectedInvoiceIds,
        }),
        [selectedInvoiceIds],
    );

    const toolbarElements = useCallback(
        (elements: ToolbarOptions[]) => [
            ...elements,
            <SingleSelect<InvoiceTableModes>
                key="tableMode"
                name="tableMode"
                items={availableTableModes}
                value={tableModeValue}
                onChange={setTableMode}
            />,
        ],
        [availableTableModes, setTableMode, tableModeValue],
    );

    return (
        <>
            <TableWithStoredSettingsAndLayoutSettings
                layoutSettingsContext={EnumFrontendLayoutSetting.OpenInvoicesTableLayout}
                items={emptyItems}
                name={'ExportInvoiceList'}
                columns={exportInvoiceColumns}
                initialSorting={initialInvoiceSorting}
                loader={invoicesListApi.loadPage}
                refetchRef={invoicesRefetchRef}
                canCreate={hasPermission(Permission.BillingInvoicesCreate)}
                objectSelector={objectSelector}
                toolbarElements={toolbarElements}
                useOnClickNavigation
                fullSize
                maxHeight="60vH"
            />

            <Card name="exports" label="Letzte Exporte" maxWidth="100%" className="mt-5">
                <Table<ApiDynamicExportRun>
                    name="last-exports"
                    columns={exportColumns}
                    loader={exportsListApi.loadPage}
                    onSelect={openRunDetail}
                    refetchRef={exportRefetchRef}
                    initialSorting={{ columnName: 'created_at', direction: 'desc' }}
                    disableTopToolbar
                    maxHeight="20vh"
                />
            </Card>

            <FloatingToolbar isOpen={hasSelectedInvoices}>
                <Badge size="sm" type="input" variant="primary">
                    {selectedInvoiceIds.length.toString()}
                </Badge>
                Ausgewählt
                <Button
                    appearance="filled"
                    variant="success"
                    onClick={handleExport}
                    locked={isExporting || !hasExportOptions}
                >
                    <IconAbacus size={defaultIconSize} />
                    &nbsp;{isExporting ? 'Exportiere...' : 'Exportieren'}
                </Button>
            </FloatingToolbar>
        </>
    );
}
